import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  LinearProgress
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Roomrates() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState({});
  const [roomPlans, setRoomPlans] = useState([]);
  const [rates, setRates] = useState({});
  const [discounts, setDiscounts] = useState({}); // Added state for discounts
  const [savingRoomId, setSavingRoomId] = useState(null);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      setUserId(user.uid);

      const snap = await getDocs(collection(db, "authroles"));
      snap.forEach(d => {
        if (d.id === user.uid) {
          setPropertyId(d.data().propertyId);
        }
      });
    });
    return () => unsub();
  }, []);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!userId || !propertyId) return;
    loadAll();
  }, [userId, propertyId]);

  const loadAll = async () => {
    /* ROOMS */
    const roomSnap = await getDocs(
      query(
        collection(db, "rooms"),
        where("propertyId", "==", propertyId)
      )
    );
    setRooms(roomSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    /* RATE PLANS */
    const planSnap = await getDocs(collection(db, "rate_plans"), where("propertyId", "==", propertyId));
    const planMap = {};
    planSnap.docs.forEach(d => {
      planMap[d.id] = d.data().planName;
    });
    setPlans(planMap);

    /* ROOM ↔ PLAN */
    const tariffSnap = await getDocs(
      query(
        collection(db, "room_tariffs"),
        where("propertyId", "==", propertyId)
      )
    );
    setRoomPlans(tariffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    /* EXISTING FIXED RATES & DISCOUNTS */
    const rateSnap = await getDocs(
      query(
        collection(db, "roomrates"),
        where("propertyId", "==", propertyId)
      )
    );

    const rateMap = {};
    const discountMap = {};
    
    rateSnap.docs.forEach(d => {
      const r = d.data();
      const key = `${r.roomId}_${r.ratePlanId}`;
      rateMap[key] = r.rate;
      discountMap[key] = r.discount || ""; // Fetching discount value
    });
    
    setRates(rateMap);
    setDiscounts(discountMap);
  };

  /* ================= CHANGE VALUE HANDLERS ================= */
  const changeRate = (roomId, planId, val) => {
    setRates(p => ({ ...p, [`${roomId}_${planId}`]: val }));
  };

  const changeDiscount = (roomId, planId, val) => {
    setDiscounts(p => ({ ...p, [`${roomId}_${planId}`]: val }));
  };

  /* ================= SAVE ================= */
  const saveRoom = async (roomId) => {
    try {
      setSavingRoomId(roomId);

      const plansForRoom = roomPlans.filter(p => p.roomId === roomId);

      for (let p of plansForRoom) {
        const key = `${roomId}_${p.ratePlanId}`;
        
        // Save if either rate or discount exists
        if (!rates[key] && !discounts[key]) continue;

        await setDoc(
          doc(db, "roomrates", key),
          {
            roomId,
            ratePlanId: p.ratePlanId,
            planName: plans[p.ratePlanId],
            rate: rates[key] ? Number(rates[key]) : 0,
            discount: discounts[key] ? Number(discounts[key]) : 0, // Saving discount field
            userId,
            propertyId,
            updatedAt: new Date()
          }
        );
      }

      toast.success("Rates and discounts saved successfully");
    } catch (error) {
      console.error(error);
      toast.error("Save failed");
    } finally {
      setSavingRoomId(null);
    }
  };

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box className="content-wrapper" flex={1} p={3}>
        <ToastContainer autoClose={2000} />

        <Typography variant="h5" fontWeight={800} mb={2}>
          Room Fixed Rates & Discounts
        </Typography>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent className="row" sx={{ display: "flex", flexWrap: "wrap", gap: 3, p: 3 }}>

            {rooms.map(room => {
              const plansForRoom = roomPlans.filter(p => p.roomId === room.id);

              return (
                <Card
                  className="col-sm-6"
                  key={room.id}
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #eee",
                    flex: "1 1 calc(50% - 24px)",
                    minWidth: "300px"
                  }}
                >
                  <CardContent>

                    {/* Room Title */}
                    <Typography
                      fontWeight={800}
                      fontSize={16}
                      mb={2}
                      color="primary"
                    >
                      {room.roomTypeName}
                    </Typography>

                    {/* Rate and Discount Rows */}
                    {plansForRoom.map((p) => {
                      const key = `${room.id}_${p.ratePlanId}`;
                      return (
                        <Box
                          key={p.id}
                          display="flex"
                          alignItems="center"
                          gap={2}
                          mb={1.5}
                        >
                          {/* Plan Name */}
                          <Box
                            width={150}
                            fontWeight={600}
                            color="text.secondary"
                            noWrap
                          >
                            {plans[p.ratePlanId]}
                          </Box>

                          {/* Rate Field */}
                          <TextField
                            size="small"
                            type="number"
                            label="Rate"
                            sx={{ width: 120 }}
                            value={rates[key] || ""}
                            onChange={e => changeRate(room.id, p.ratePlanId, e.target.value)}
                          />

                          {/* Discount Field */}
                          <TextField
                            size="small"
                            type="number"
                            label="Discount"
                            sx={{ width: 120 }}
                            value={discounts[key] || ""}
                            onChange={e => changeDiscount(room.id, p.ratePlanId, e.target.value)}
                          />
                        </Box>
                      );
                    })}

                    {/* Unified Room Action Button Section */}
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ minWidth: 120 }}
                        disabled={savingRoomId === room.id}
                        onClick={() => saveRoom(room.id)}
                      >
                        {savingRoomId === room.id ? "Saving..." : "Save Room Data"}
                      </Button>
                    </Box>

                    {/* Loader */}
                    {savingRoomId === room.id && (
                      <LinearProgress
                        sx={{
                          mt: 2,
                          borderRadius: 1
                        }}
                      />
                    )}

                  </CardContent>
                </Card>
              );
            })}

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomrates;